import { Component, Input, ViewChild, Inject, Output, EventEmitter } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';


import { MessageService } from 'primeng/api';
import { provideMarkdown, MARKED_OPTIONS, MarkedOptions, MarkedRenderer, MarkdownService } from 'ngx-markdown';

import { AnswerEngineOption } from '../../models/answer-engine-option.model';
import { AnswerEngineTypesEnum } from '../../enums/answer-engine-types.enum';
import { TabsComponent } from '@Common/Layout/components/Tabs/tabs.component';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { copy } from '../../../Common/Misc/helpers/copy/copy.helper';
import { htmlToFormattedText } from '../../../Common/Misc/helpers/html/html.helper';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import {
    ManageListDialogComponent
} from '../../../Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { TargetingResultModel } from '../../../Discover/Targeting/models/targeting-result.model';
import { TargetingRhpDialogService } from '../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';


/**
 * Opens answer links in a new tab, styled as external links
 */
export function viAnswerMarkedOptionsFactory(): MarkedOptions {
    const renderer = new MarkedRenderer();
    const defaultLink = renderer.link;

    renderer.link = function (token) {
        return defaultLink.call(this, token)
            .replace(/^<a /, '<a target="_blank" rel="noopener" class="agpr-viAnswerPanel-externalLink" ');
    };

    return { renderer, gfm: true, breaks: true };
}


/**
 * The parent owns the engine state, acting as the SSOT
 */
@Component({
    selector: 'agpr-vi-answer-panel',
    templateUrl: './vi-answer-panel.component.html',
    styleUrls: ['./vi-answer-panel.component.scss'],
    standalone: false,
    providers: [
        provideMarkdown({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: viAnswerMarkedOptionsFactory,
            },
        }),
    ],
})
export class ViAnswerPanelComponent {

    @Input() engines: AnswerEngineOption[] = [];
    @Input() selectedType: AnswerEngineTypesEnum | null = null;

    @Output() selectedTypeChange = new EventEmitter<AnswerEngineTypesEnum>();

    @ViewChild('answerContent') answerContent: TabsComponent;

    readonly buttonType = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    readonly id = 'aiAnswerPanel';
    readonly answerTabId = this.id + '_answerTab';
    readonly sourcesTabId = this.id + '_sourcesTab';

    hasTargetingPermission: boolean = false;
    selectedTabId: string = '';

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private messageService: MessageService,
        private coreDialogService: CoreDialogService,
        private targetingRhpDialogService: TargetingRhpDialogService,
        private markdownService: MarkdownService,
    ) {}


    get selectedEngine(): AnswerEngineOption | undefined {
        return this.engines.find(e => e.type === this.selectedType) ?? this.engines[0];
    }

    select(engineType: AnswerEngineTypesEnum) {
        this.selectedTypeChange.emit(engineType);

        requestAnimationFrame(() => {
            this.answerContent.scrollPanel.scrollTo(0);
        });
    }

    onTabChanged(tab: any) {
        this.selectedTabId = tab.id;
    }

    async copyAnswer(event: Event, answer: string, removeBrTag = false) {
        if (!answer) {
            return;
        }

        const answerHtml = await this.markdownService.parse(answer);
        const formattedText = htmlToFormattedText(answerHtml, removeBrTag);
        copy.text(formattedText);

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Common.dialog.aeoScout.answerCopied.summary',
            detail: 'Common.dialog.aeoScout.answerCopied.detail',
        });

        (event.target as HTMLElement)?.focus();
    }

    expandOutletProfile(outletId: string) {
        const io = {
            inputs: {
                outletId: outletId,
                entityType: EntityType.OUTLET,
                fullHeight: true,
                disableContentToggle: true
            },
            outputs: {
                dialogClose: () => {
                }
            }
        };
        this.targetingRhpDialogService.openTakeOver(io);
    }

    copyOutletUrl(url: string) {
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Common.url.copied.summary',
            detail: 'Common.url.copied.detail',
        });
        copy.text(url);
    }

    openManageListDialog(entity: TargetingResultModel) {
        this.coreDialogService.open(ManageListDialogComponent, {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        });
    }
}
